/**
 * BrandAssetsPanel — shared brand-asset library (Cloudinary-backed).
 *
 * Admins upload assets with a stable key + label; the public brand manifest
 * (/api/public/brand/manifest.json → custom_assets) exposes their CDN URLs so
 * ERIO references them by key.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, UploadCloud, Copy, Trash2, FileText, ExternalLink } from "lucide-react";
import api from "../../lib/api";

const isImage = (a) => a.type === "image" && !["pdf"].includes((a.format || "").toLowerCase());

const BrandAssetsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/brand-assets");
      setItems(data.items || []);
    } catch {
      toast.error("Couldn't load brand assets.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !key.trim() || !label.trim()) {
      toast.error("Key, label and a file are all required.");
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    form.append("key", key.trim().toLowerCase());
    form.append("label", label.trim());
    try {
      await api.post("/admin/brand-assets", form);
      toast.success("Asset uploaded.");
      setKey(""); setLabel(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (k) => {
    if (!window.confirm(`Delete asset "${k}"? ERIO and any manifest consumers will lose this URL.`)) return;
    try {
      await api.delete(`/admin/brand-assets/${k}`);
      toast.success("Asset deleted.");
      setItems((prev) => prev.filter((i) => i.key !== k));
    } catch {
      toast.error("Delete failed.");
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(url).then(() => toast.success("URL copied")).catch(() => toast.error("Copy failed."));
  };

  const inputCls = "w-full mt-1 border border-zinc-300 px-3 py-2 text-[14px] text-black focus:border-[#7737F6] outline-none";

  return (
    <div data-testid="brand-assets-panel">
      <div className="flex items-start gap-3 mb-6">
        <ImageIcon className="text-[#7737F6] mt-1" size={20} />
        <div>
          <h2 className="ek-heading text-2xl text-black">Brand assets</h2>
          <p className="ek-body text-[14px] mt-1 max-w-2xl">
            Upload shared brand assets (logos, banners, cover art, PDFs). Each gets a stable key and is
            published in the public brand manifest so ERIO can reference it by key —{" "}
            <code className="text-[12px] bg-zinc-100 px-1">/api/public/brand/manifest.json</code> → <code className="text-[12px] bg-zinc-100 px-1">custom_assets</code>.
          </p>
        </div>
      </div>

      <form onSubmit={upload} className="bg-white border border-zinc-200 p-5 mb-8 grid md:grid-cols-3 gap-4 items-end" data-testid="brand-asset-upload-form">
        <div>
          <label className="ek-label">Key (slug)</label>
          <input value={key} onChange={(e) => setKey(e.target.value)} placeholder="primary-logo" className={inputCls} data-testid="brand-asset-key" />
        </div>
        <div>
          <label className="ek-label">Label</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Primary logo (horizontal)" className={inputCls} data-testid="brand-asset-label" />
        </div>
        <div>
          <label className="ek-label">File</label>
          <input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className={inputCls} data-testid="brand-asset-file" />
        </div>
        <div className="md:col-span-3">
          <button type="submit" disabled={uploading} className="ek-btn ek-btn-primary text-[12px] px-6 py-3 inline-flex items-center gap-2 disabled:opacity-50" data-testid="brand-asset-upload-btn">
            <UploadCloud size={14} /> {uploading ? "Uploading…" : "Upload asset"}
          </button>
        </div>
      </form>

      {loading ? (
        <p className="text-zinc-500 text-sm" data-testid="brand-assets-loading">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-white p-10 text-center border border-zinc-200" data-testid="brand-assets-empty">
          <h3 className="ek-heading text-xl text-black">No assets yet.</h3>
          <p className="ek-body text-[14px] mt-3 max-w-md mx-auto">Upload your first brand asset above — it'll appear in the public manifest immediately.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="brand-assets-grid">
          {items.map((a) => (
            <div key={a.key} className="border border-zinc-200 bg-white" data-testid={`brand-asset-card-${a.key}`}>
              <div className="h-40 bg-[#F2F2F3] flex items-center justify-center overflow-hidden">
                {isImage(a) ? (
                  <img src={a.url} alt={a.label} className="ek-logo-on-dark max-h-40 max-w-full object-contain" />
                ) : (
                  <FileText size={40} className="text-zinc-400" />
                )}
              </div>
              <div className="p-3">
                <div className="text-[14px] font-medium text-black truncate">{a.label}</div>
                <div className="text-[11.5px] text-zinc-400 mt-0.5">
                  <code className="bg-zinc-100 px-1">{a.key}</code> · {a.type}{a.format ? `/${a.format}` : ""}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button type="button" onClick={() => copyUrl(a.url)} className="ek-btn ek-btn-outline-dark text-[11px] px-2.5 py-1.5 inline-flex items-center gap-1.5 flex-1 justify-center" data-testid={`brand-asset-copy-${a.key}`}>
                    <Copy size={11} /> Copy URL
                  </button>
                  <a href={a.url} target="_blank" rel="noopener noreferrer" className="ek-btn ek-btn-outline-dark text-[11px] px-2.5 py-1.5 inline-flex items-center justify-center" aria-label="Open" data-testid={`brand-asset-open-${a.key}`}>
                    <ExternalLink size={11} />
                  </a>
                  <button type="button" onClick={() => remove(a.key)} className="ek-btn text-[11px] px-2.5 py-1.5 inline-flex items-center justify-center border border-rose-200 text-rose-600 hover:bg-rose-50" aria-label="Delete" data-testid={`brand-asset-delete-${a.key}`}>
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BrandAssetsPanel;
